import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {Observable, takeUntil} from 'rxjs';
import {selectAddOns} from '../../../../add-ons/reducers';
import {
  CartItem,
  CartItemInterface,
  Image,
  NO_IMAGE_PLACEHOLDER,
  OnDestroySubject,
  ProductComponentModel,
  serviceOfferComponentKey,
} from '../../../../data/model';
import type {Product, ProductType} from '../../../../data/model/product';
import {
  BreakpointUtilityService,
  EnvironmentConfiguration,
  filterNullOrUndefined,
} from '../../../service';
import * as cartActions from '../../cart.actions';
import {buildCartItemSpecLabel} from '../../cart-item-spec-label';
import {Router} from '@angular/router';

interface CartItemImageSource {
  image?: Image;
  images?: Image[];
  logo?: Image | null;
}

@Component({
  selector: 'app-cart-item',
  templateUrl: './cart-item.component.html',
  styleUrls: ['./cart-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-cart-item-component'},
  standalone: false,
})
export class CartItemComponent
  extends OnDestroySubject
  implements OnChanges, OnInit, AfterViewChecked
{
  @Input() editable = true;
  @Input() checkoutPage = false;
  @Input() companionMode = false;
  item = input.required<CartItem>();

  specsLabel = computed(() => buildCartItemSpecLabel(this.item()));
  fulfillmentImgURLBase = EnvironmentConfiguration.commerceImageBase();
  cartItemForm = new FormGroup({
    id: new FormControl<string>('', [Validators.required]),
    productId: new FormControl<string>('', [Validators.required]),
    quantity: new FormControl<number>(1, [Validators.required]),
    name: new FormControl<string>(''),
  });
  addOn$: Observable<ProductComponentModel<ProductType>[] | undefined>;
  catalogProducts: Array<Product & {bundleId?: number}> = [];
  maxQuantity: number | undefined;

  constructor(
    protected breakpointUtils: BreakpointUtilityService,
    private store: Store,
    private router: Router,
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {
    super();
    this.addOn$ = this.store.pipe(
      select(selectAddOns),
      filterNullOrUndefined(),
    );
  }

  get quantityControl(): FormControl<number> {
    return this.cartItemForm.get('quantity') as FormControl;
  }

  get quantityStepperMax(): number {
    return this.maxQuantity ?? Number.MAX_SAFE_INTEGER;
  }

  ngAfterViewChecked(): void {
    if (this.companionMode) {
      const inputs = this.elementRef.nativeElement.querySelectorAll(
        '.se-fe-form-control-quantity--size-4',
      );
      inputs.forEach((el: HTMLElement) => {
        this.renderer.setStyle(el, 'width', 'calc(1.5rem + 0.7em * 2)');
      });

      const labels = this.elementRef.nativeElement.querySelectorAll(
        'se-fe-form-field-quantity label.se-fe-form-control-label',
      );
      labels.forEach((el: HTMLElement) => {
        this.renderer.setStyle(el, 'display', 'none');
      });
    }
  }

  ngOnInit(): void {
    this.addOn$
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value: ProductComponentModel<ProductType>[] | undefined) => {
        this.catalogProducts = (value ?? []).flatMap(component => {
          const data = component.data;
          if (data === undefined || data === null) {
            return [];
          }
          const rows = Array.isArray(data) ? data : [data];
          return rows;
        });
        this.maxQuantity = this.bundleOrProduct()?.maxQuantity ?? undefined;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.item) return;

    this.bindFormValue(changes.item.currentValue);
  }

  showParticipantName(): boolean {
    return (
      !this.isBundleGroup() &&
      !!this.item().references?.participant &&
      !!this.item().quote
    );
  }

  updateQuantity(): void {
    if (this.isBundleGroup()) {
      const bundleGroupItems = this.item().bundleGroupItems ?? [];
      if (this.quantityControl.value === 0) {
        this.removeItem();
      } else if (bundleGroupItems.length) {
        this.store.dispatch(
          cartActions.updateBundleItemsQuantity({
            items: bundleGroupItems,
            quantity: this.quantityControl.value,
            bundleDisplayName: this.displayName(),
          }),
        );
      }
      return;
    }

    if (this.quantityControl.value === 0) {
      this.removeItem();
    } else {
      const item = new CartItem({
        ...this.item(),
        ...(<CartItemInterface>this.cartItemForm.value),
      });
      this.store.dispatch(cartActions.updateCartItem({item}));
    }
  }

  removeItem(): void {
    const bundleGroupItems = this.item().bundleGroupItems ?? [];
    if (this.isBundleGroup() && bundleGroupItems.length) {
      this.store.dispatch(
        cartActions.removeBundleItemsFromCart({
          items: bundleGroupItems,
          bundleDisplayName: this.displayName(),
        }),
      );
      return;
    }

    const item = new CartItem({
      ...this.item(),
      ...(<CartItemInterface>this.cartItemForm.value),
    });
    this.store.dispatch(
      cartActions.removeItemFromCart({
        item,
      }),
    );
  }

  hideItemQuantity(): boolean {
    return (
      !this.isBundleGroup() &&
      this.item().templateKey === serviceOfferComponentKey
    );
  }

  isBundleGroup(): boolean {
    return (this.item().bundleGroupItems?.length ?? 0) > 0;
  }

  bundleGroupLines(): string[] {
    return this.item().bundleGroupLines ?? [];
  }

  /**
   * Line total for this cart row. Bundle groups sum child lines; when those
   * still resolve to zero (e.g. missing unit price on API lines), uses the
   * bundle product price from add-ons when available.
   */
  displayPrice(): number {
    const bundleGroupItems = this.item().bundleGroupItems ?? [];
    let result = this.item().price();

    if (this.isBundleGroup() && bundleGroupItems.length) {
      const sumFromChildren = bundleGroupItems.reduce(
        (sum, bundleItem) => sum + bundleItem.price(),
        0,
      );
      const catalogBundle = this.bundleOrProduct();
      const catalogPrice = catalogBundle?.price;
      const qty = this.item().quantity || 1;
      const hasValidCatalogPrice =
        typeof catalogPrice === 'number' &&
        Number.isFinite(catalogPrice) &&
        catalogPrice > 0;
      const fallbackCatalog = hasValidCatalogPrice ? catalogPrice * qty : null;

      result =
        sumFromChildren > 0
          ? sumFromChildren
          : fallbackCatalog !== null
            ? fallbackCatalog
            : sumFromChildren;
    }

    return result;
  }

  displayName(): string {
    if (!this.isBundleGroup()) {
      return this.item().name ?? '';
    }

    const snapshotName = this.item().presentationSnapshot?.name;
    if (typeof snapshotName === 'string' && snapshotName.trim().length > 0) {
      return snapshotName;
    }

    const bundleName = this.item().bundle_name;
    if (typeof bundleName === 'string' && bundleName.trim().length > 0) {
      return bundleName;
    }

    return this.bundleOrProduct()?.name ?? this.item().name ?? 'Bundle';
  }

  cartItemImage(): Image {
    return this.cartItemImageFor(this.item()) ?? NO_IMAGE_PLACEHOLDER;
  }

  bundlePreviewImage(): Image {
    if (!this.isBundleGroup()) {
      return this.cartItemImage();
    }

    return this.bundleDisplayImage();
  }

  /** Picks the first image from item.images or item.image. */
  private getCartItemBaseImage(item: CartItemImageSource): Image | undefined {
    const image = (item.images ?? []).find(image => image?.src?.trim());
    return image ?? item.logo ?? item.image;
  }

  /** True when the URL should be used as-is (not prefixed with the image CDN base). */
  private isAbsoluteOrActivationUrl(src: string): boolean {
    return (
      src.includes('activation') ||
      src.includes('https') ||
      src.startsWith('data:')
    );
  }

  private cartItemImageFor(
    item: CartItemImageSource | undefined,
    includeFallback = true,
  ): Image | undefined {
    if (!item) {
      return includeFallback ? NO_IMAGE_PLACEHOLDER : undefined;
    }

    const baseImage = this.getCartItemBaseImage(item);
    if (!baseImage?.src) {
      return includeFallback ? NO_IMAGE_PLACEHOLDER : undefined;
    }
    if (this.isAbsoluteOrActivationUrl(baseImage.src)) {
      return baseImage;
    }
    return {
      src: this.fulfillmentImgURLBase + String(baseImage.src),
      alt: String(baseImage.alt),
    };
  }

  private bundleDisplayImage(): Image {
    return (
      this.cartItemImageFor(this.item().presentationSnapshot, false) ??
      this.cartItemImageFor(this.bundleOrProduct(), false) ??
      this.cartItemImageFor(this.item(), false) ??
      NO_IMAGE_PLACEHOLDER
    );
  }

  private bundleOrProduct(): (Product & {bundleId?: number}) | undefined {
    if (this.isBundleGroup()) {
      return this.findBundleProduct(this.item().bundleId);
    }

    return this.catalogProducts.find(
      addOn => String(addOn.id) === String(this.item().productId),
    );
  }

  private findBundleProduct(
    bundleId: number | undefined,
  ): (Product & {bundleId?: number}) | undefined {
    if (bundleId === undefined) {
      return undefined;
    }

    return this.catalogProducts.find(addOn => {
      const addOnWithBundle = addOn;
      return (
        addOnWithBundle.bundleId === bundleId ||
        Number(addOnWithBundle.id) === bundleId
      );
    });
  }

  private bindFormValue(value: CartItem): void {
    this.cartItemForm.patchValue(value);
  }
}
